import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { X, Bell } from 'lucide-react';
import { PageLayout } from '../../components/layout/PageLayout';
import { useTranslation } from '../../hooks/useTranslation';
import { useI18n } from '../../contexts/I18nContext';

interface Notification {
  id: string;
  title: string;
  body: string;
  created_at: string;
  read: boolean;
  type: string | null;
  trade_id: string | null;
  conversation_id: string | null;
}

export const Notifications = () => {
  const { t } = useTranslation();
  const { currentLang } = useI18n();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  const fetchNotifications = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (!error && data) {
        setNotifications(data);
        // Marquer les notifications comme lues
        await supabase
          .from('notifications')
          .update({ read: true })
          .eq('user_id', user.id)
          .eq('read', false);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();

    // Écouter les changements sur les notifications
    const channel = supabase
      .channel('notifications_changes')
      .on(
        'postgres_changes',
        {
          event: '*', // INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user?.id}`
        },
        () => {
          // Recharger les notifications à chaque changement
          fetchNotifications();
        }
      )
      .subscribe();

    return () => {
      console.log('Désinscription du canal de notifications');
      supabase.removeChannel(channel);
    };
  }, [user]);

  const handleDelete = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId)
        .eq('user_id', user?.id);

      if (error) throw error;

      // Mettre à jour l'état local après la suppression
      setNotifications(notifications.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Erreur lors de la suppression de la notification:', error);
    }
  };

  const handleDeleteAll = async () => {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('user_id', user?.id);

      if (error) throw error;

      // Vider la liste des notifications
      setNotifications([]);
    } catch (error) {
      console.error('Erreur lors de la suppression des notifications:', error);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    try {
      // Marquer la notification comme lue
      if (!notification.read) {
        const { error } = await supabase
          .from('notifications')
          .update({ read: true })
          .eq('id', notification.id)
          .eq('user_id', user?.id);

        if (error) throw error;

        setNotifications(notifications.map(n => 
          n.id === notification.id ? { ...n, read: true } : n
        ));
      }

      // Naviguer vers la page appropriée selon le type de notification
      if (notification.type === 'trade_accepted' && notification.trade_id) {
        navigate(`/trades/${notification.trade_id}`);
      } else if (notification.type === 'new_message' && notification.conversation_id) {
        // Redirection vers la conversation
        navigate(`/messages/${notification.conversation_id}`);
      }
    } catch (error) {
      console.error('Erreur lors du traitement de la notification:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', user?.id)
        .eq('read', false);

      if (error) throw error;

      // Mettre à jour l'état local
      setNotifications(notifications.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Erreur lors de la mise à jour des notifications:', error);
    }
  };

  const getNotificationContent = (notification: Notification) => (
    <>
      <h3 className="font-semibold">{notification.title}</h3>
      <p className="text-gray-800">{notification.body}</p>
    </>
  );

  if (loading) {
    return (
      <PageLayout disableAds={true}>
        <div className="min-h-[calc(100vh-16rem)] px-4 py-8 md:py-12">
          <div className="max-w-2xl mx-auto">
            <div className="animate-pulse space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-1/2" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout disableAds={true}>
      <div className="min-h-[calc(100vh-16rem)]">
        <div className="max-w-2xl mx-auto px-4 py-8 md:py-12">
          {/* En-tête */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-3">
              <Bell className="w-6 h-6 text-primary-600" />
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">
                {t('notifications.title')}
              </h1>
            </div>
            <div className="flex gap-2 md:gap-4">
              {notifications.some(n => !n.read) && (
                <button
                  onClick={markAllAsRead}
                  className="text-sm text-primary-600 hover:text-primary-800 transition-colors px-3 py-1.5 rounded-lg hover:bg-primary-50"
                >
                  {t('notifications.mark_all_read')}
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  onClick={handleDeleteAll}
                  className="text-sm text-red-600 hover:text-red-800 transition-colors px-3 py-1.5 rounded-lg hover:bg-red-50"
                >
                  {t('notifications.delete_all')}
                </button>
              )}
            </div>
          </div>

          {/* Liste des notifications */}
          <div className={`space-y-3 ${notifications.length === 0 ? 'flex items-center justify-center' : ''}`}>
            {notifications.length === 0 ? (
              <div className="text-center">
                <Bell className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">{t('notifications.empty_state')}</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`relative group cursor-pointer rounded-lg shadow-sm transition-all hover:shadow-md border border-gray-100 ${
                    notification.read ? 'bg-white' : 'bg-blue-50'
                  }`}
                >
                  <div className="p-4">
                    <div className="pr-8">
                      <h3 className={`text-sm md:text-base ${!notification.read && 'font-medium'}`}>
                        {notification.title}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {notification.body}
                      </p>
                      <p className="text-xs text-gray-400 mt-2">
                        {new Date(notification.created_at).toLocaleDateString(currentLang === 'en' ? 'en-US' : 'fr-FR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(notification.id);
                      }}
                      className="absolute top-4 right-4 p-1 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity rounded-full hover:bg-red-50"
                      aria-label={t('notifications.delete_notification')}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  );
}; 